/* eslint-disable react-refresh/only-export-components -- kit de UI: tokens (COLORS) + componentes conviven a propósito en un único módulo cohesivo */
// ════════════════════════════════════════════════════════════════════════════
// M2.7 Madurez de Procesos — kit visual del Análisis IA premium (réplica de M1.5)
// Sistema de tarjetas COHESIVO + render markdown + matriz DAFO + resumen ejecutivo.
// DUPLICADO de structure/.../analisis-ui.tsx por estanqueidad de módulos: process/
// NO puede importar de structure/. Mantener en paridad visual con el original.
// Design system V·IAnet: DM Sans, paleta AZUL, SVG/HTML inline, cero Tailwind.
// ════════════════════════════════════════════════════════════════════════════
import type { ReactNode } from 'react';

export const COLORS = {
  a1: '#1F3864', // azul profundo (títulos)
  a2: '#2F5496', // azul medio (acentos)
  cyan: '#33BCE1', // cian (resaltes)
  go: '#595959', // gris texto secundario
  gm: '#D9D9D9', // gris borde
  gc: '#F2F2F2', // gris fondo celda
  w: '#FFFFFF',
  verde: '#16A34A',
  ambar: '#E0A800',
  rojo: '#DC2626',
  ink: '#1A1A1A', // texto principal
} as const;

const FONT = "'DM Sans', sans-serif";

// ── Tarjeta de sección unificada ────────────────────────────────────────────
export function SectionCard({
  title,
  hint,
  accent = COLORS.a2,
  right,
  children,
}: {
  title: string;
  hint?: string;
  accent?: string;
  right?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section
      style={{
        background: COLORS.w,
        border: `1px solid ${COLORS.gm}`,
        borderRadius: 10,
        padding: 16,
        fontFamily: FONT,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          marginBottom: 10,
          paddingBottom: 8,
          borderBottom: `1px solid ${COLORS.gc}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span
            style={{ width: 4, height: 16, borderRadius: 2, background: accent, flexShrink: 0 }}
          />
          <h3
            style={{
              margin: 0,
              fontSize: 12,
              fontWeight: 800,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              color: COLORS.a1,
            }}
          >
            {title}
          </h3>
          {hint && <span style={{ fontSize: 11, color: COLORS.go }}>· {hint}</span>}
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

// ── Badge ────────────────────────────────────────────────────────────────────
type Tono = 'verde' | 'ambar' | 'rojo' | 'azul' | 'neutro';
const TONO: Record<Tono, { bg: string; fg: string }> = {
  verde: { bg: '#E7F6EE', fg: '#0F6E56' },
  ambar: { bg: '#FFF8E6', fg: '#7A5C00' },
  rojo: { bg: '#FDECEC', fg: '#7B0000' },
  azul: { bg: '#EAF1FB', fg: COLORS.a1 },
  neutro: { bg: COLORS.gc, fg: COLORS.go },
};
export function Badge({ children, tono = 'neutro' }: { children: ReactNode; tono?: Tono }) {
  const c = TONO[tono];
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        padding: '2px 8px',
        borderRadius: 10,
        background: c.bg,
        color: c.fg,
        whiteSpace: 'nowrap',
        fontFamily: FONT,
      }}
    >
      {children}
    </span>
  );
}

// ── Render markdown ligero (titulares ###, **negrita**, viñetas) ─────────────
function inline(text: string | null | undefined, keyBase: string): ReactNode[] {
  if (!text) return []; // defensa: un ítem null/undefined no debe romper el render
  // Divide por **negrita** y alterna texto normal / fuerte.
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={`${keyBase}-b${i}`} style={{ color: COLORS.a1 }}>
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <span key={`${keyBase}-t${i}`}>{part}</span>;
  });
}

export function Markdown({ text }: { text: string }) {
  const lineas = text.replace(/\r\n/g, '\n').split('\n');
  const bloques: ReactNode[] = [];
  let lista: string[] = [];
  const cerrarLista = () => {
    if (lista.length) {
      const items = lista;
      bloques.push(
        <ul key={`ul-${bloques.length}`} style={{ margin: '4px 0 8px', paddingLeft: 18 }}>
          {items.map((li, i) => (
            <li
              key={i}
              style={{ fontSize: 13, color: COLORS.ink, lineHeight: 1.5, marginBottom: 2 }}
            >
              {inline(li, `li-${bloques.length}-${i}`)}
            </li>
          ))}
        </ul>,
      );
      lista = [];
    }
  };
  for (const cruda of lineas) {
    const l = cruda.trimEnd();
    if (!l.trim()) {
      cerrarLista();
      continue;
    }
    const h = l.match(/^(#{1,4})\s+(.*)$/);
    const bullet = l.match(/^[-*]\s+(.*)$/);
    if (h) {
      cerrarLista();
      const nivel = h[1].length;
      bloques.push(
        <div
          key={`h-${bloques.length}`}
          style={{
            fontSize: nivel <= 2 ? 14 : 12,
            fontWeight: 800,
            color: COLORS.a1,
            margin: bloques.length ? '10px 0 4px' : '0 0 4px',
            textTransform: nivel <= 2 ? 'none' : 'uppercase',
            letterSpacing: nivel <= 2 ? 0 : 0.3,
          }}
        >
          {inline(h[2].replace(/\*\*/g, ''), `h-${bloques.length}`)}
        </div>,
      );
    } else if (bullet) {
      lista.push(bullet[1]);
    } else {
      cerrarLista();
      bloques.push(
        <p
          key={`p-${bloques.length}`}
          style={{ fontSize: 13, color: COLORS.ink, lineHeight: 1.55, margin: '0 0 8px' }}
        >
          {inline(l, `p-${bloques.length}`)}
        </p>,
      );
    }
  }
  cerrarLista();
  return <div style={{ fontFamily: FONT }}>{bloques}</div>;
}

// ── Lista punteada (fortalezas / riesgos) ────────────────────────────────────
export function ListaPunteada({ color, items }: { color: string; items: string[] | null }) {
  if (!items || items.length === 0) {
    return <div style={{ fontSize: 12, color: COLORS.go, fontStyle: 'italic' }}>—</div>;
  }
  return (
    <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', fontFamily: FONT }}>
      {items.map((it, i) => (
        <li
          key={i}
          style={{ display: 'flex', gap: 8, fontSize: 13, color: COLORS.ink, marginBottom: 6 }}
        >
          <span style={{ color, fontWeight: 900, lineHeight: 1.4, flexShrink: 0 }}>•</span>
          <span style={{ lineHeight: 1.45 }}>{inline(it, `lp-${i}`)}</span>
        </li>
      ))}
    </ul>
  );
}

// ── Matriz DAFO 2×2 ──────────────────────────────────────────────────────────
export interface DafoData {
  fortalezas: string[];
  debilidades: string[];
  oportunidades: string[];
  amenazas: string[];
}
function Cuadrante({
  titulo,
  signo,
  color,
  bg,
  items,
}: {
  titulo: string;
  signo: string;
  color: string;
  bg: string;
  items: string[];
}) {
  return (
    <div style={{ background: bg, borderRadius: 8, padding: 12, minHeight: 96 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 800, color }}>{titulo}</span>
        <span style={{ fontSize: 11, fontWeight: 800, color, opacity: 0.7 }}>{signo}</span>
      </div>
      {items.length === 0 ? (
        <div style={{ fontSize: 11, color: COLORS.go, fontStyle: 'italic' }}>—</div>
      ) : (
        <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
          {items.slice(0, 5).map((it, i) => (
            <li
              key={i}
              style={{ display: 'flex', gap: 6, fontSize: 12, color: COLORS.ink, marginBottom: 4 }}
            >
              <span style={{ color, fontWeight: 900, flexShrink: 0 }}>›</span>
              <span style={{ lineHeight: 1.4 }}>{inline(it, `cq-${i}`)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
export function DAFOMatrix({ data }: { data: DafoData }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontFamily: FONT }}>
      <Cuadrante
        titulo="Fortalezas"
        signo="(interno +)"
        color={COLORS.verde}
        bg="#EAF7F0"
        items={data.fortalezas}
      />
      <Cuadrante
        titulo="Debilidades"
        signo="(interno −)"
        color={COLORS.rojo}
        bg="#FBECEC"
        items={data.debilidades}
      />
      <Cuadrante
        titulo="Oportunidades"
        signo="(externo +)"
        color={COLORS.a2}
        bg="#EAF1FB"
        items={data.oportunidades}
      />
      <Cuadrante
        titulo="Amenazas"
        signo="(externo −)"
        color={COLORS.ambar}
        bg="#FFF7E6"
        items={data.amenazas}
      />
    </div>
  );
}
